import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import TopBar from '../components/TopBar';
import StageChangeModal from '../components/StageChangeModal';
import NewLeadModal from '../components/NewLeadModal';
import BulkImportModal from '../components/BulkImportModal';
import { useCrm, LEAD_STAGES, STAGE_META, isClosedStage, isStalledLead, STALLED_DAYS } from '../lib/crm';
import { CATEGORY_META, CATEGORY_FILTERS } from '../lib/vehicleCategory';
import OwnershipChip from '../components/OwnershipChip';
import CategoryStripe from '../components/CategoryStripe';
import { ownershipOf } from '../lib/ownership';
import { useConsultantOptions } from '../lib/useDirectory';
import { useVehicleMap } from '../lib/useVehicleMap';
import { dealValue, fmtMoneyEst } from '../lib/deals';
import { sourceLabel, isSystemNote } from '../lib/leadSource';
import { useToast } from '../lib/toast';
import { dashboardService, leadsService, leadFollowUpService } from '../lib/services';
import NextEventChip from '../components/NextEventChip';
import NextEventModal from '../components/NextEventModal';

const LEAD_IMPORT_COLUMNS = [
  { key: 'firstName' }, { key: 'lastName' }, { key: 'phone', required: true },
  { key: 'email' }, { key: 'source' }, { key: 'vehicleMake' }, { key: 'vehicleModel' }, { key: 'notes' },
];
const LEAD_SAMPLE_ROW = ['Jordan', 'Smith', '0412345678', 'jordan@example.com', 'WEB_FORM', 'Toyota', 'RAV4', 'Prefers a weekend test drive'];
import { useUnreadLeadIds } from '../lib/unreadLeads';
import { apiErrorMessage } from '../lib/apiError';

const DND_MIME = 'application/x-spectraiq-lead';

/**
 * Source filter values are the backend LeadSource ENUM, with a display label.
 * The old list was ['All','Carsales','Drive.com','Facebook','Web form','Phone']
 * and filtered with `l.source === activeSource` — but l.source is 'CARSALES',
 * so picking any source silently emptied the board.
 */
const SOURCES = [
  ['All', 'All'],
  ['CARSALES', 'Carsales'],
  ['DRIVE', 'Drive'],
  ['CAREXPERT', 'CarExpert'],
  ['AUTOTRADER', 'Autotrader'],
  ['FACEBOOK_MARKETPLACE', 'Facebook'],
  ['WEB_FORM', 'Web form'],
  ['PHONE', 'Phone'],
  ['WALK_IN', 'Walk-in'],
  ['WHATSAPP', 'WhatsApp'],
  ['SMS', 'SMS'],
  ['EMAIL', 'Email'],
];
const SOURCE_LABEL = Object.fromEntries(SOURCES);

// Stages that count against the "first reply within SLA" target.
// E1: three old stages collapsed into CONTACTED, so 'early' is now two.
const EARLY_STAGES = ['INBOUND', 'CONTACTED'];

/**
 * First response (E1 / T019, Ricky). Separate from the 24h breach below, and
 * measured in minutes because that is the window that matters: an enquiry that
 * has sat for forty minutes is a problem now, not tomorrow.
 *
 * "Unactioned" is simply "still at Inbound" — the stage model defines INBOUND
 * as arrived and not yet responded to, so the stage IS the flag. Nothing else
 * has to be kept in sync with it.
 *
 * The threshold comes from Settings -> Lead follow-up (lead_sla_config
 * .first_response_minutes), the same number the escalation job raises
 * FIRST_RESPONSE_BREACH on, so the board and the alert can never disagree.
 */
function firstResponse(lead, thresholdMins, now) {
  if (lead.stage !== 'INBOUND') return null;
  const since = lead.createdAt ? new Date(lead.createdAt).getTime() : null;
  if (!since) return null;
  const mins = Math.max(0, Math.floor((now - since) / 60000));
  return { mins, breached: mins >= thresholdMins, warning: mins >= thresholdMins * 0.5 };
}

/** "8m" / "1h 20m" / "2d" — compact enough for a card chip. */
function shortMins(m) {
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return `${Math.floor(m / 1440)}d`;
}

/** SLA breach: an early-stage lead not touched in 24h. */
function isBreaching(l) {
  if (!EARLY_STAGES.includes(l.stage)) return false;
  const last = l.lastActivityAt || l.createdAt;
  return last ? (Date.now() - new Date(last).getTime() > 24 * 60 * 60 * 1000) : false;
}

/** Whole days since an ISO timestamp (for "time in stage"). */
function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return ms < 0 ? 0 : Math.floor(ms / 86400000);
}

/* ─── Lead card helpers ─── */
function relTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const m = Math.floor(Math.max(0, Date.now() - then) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return `${Math.floor(d / 30)}mo ago`;
}
function initialsOf(name) {
  return (name || '?')
    .split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase() || '?';
}
// High score = hot lead; map to a tier used for the stripe + score badge.
function scoreTier(s) {
  if (s >= 80) return 'tier-hot';
  if (s <= 40) return 'tier-cold';
  return 'tier-warm';
}
// What the tier colour means, for the score tooltip.
const TIER_MEANING = {
  'tier-hot': 'Hot (80+)',
  'tier-warm': 'Warm (41–79)',
  'tier-cold': 'Cold (40 or below)',
};

export default function LeadsPage() {
  const { leads, customers, moveLead, refreshLeads } = useCrm();
  const { toast } = useToast();
  const navigate = useNavigate();
  const unreadLeadIds = useUnreadLeadIds();
  const { options: consultantOptions } = useConsultantOptions();
  const { vehiclesById } = useVehicleMap();
  // Deep links from the dashboards: ?filter=unactioned|unassigned|no-next-step|exceptions
  // plus the dashboard-tile links ?filter=hot|new|booked (New Leads / Hot Leads /
  // Bookings tiles drill in here) and ?consultant=<id>. Read once on mount; the
  // pills below then own the state.
  const [searchParams, setSearchParams] = useSearchParams();
  // `?filter=attention` (the sidebar badge) is the "Needs attention" toggle, not a focus.
  const initialAttention = searchParams.get('filter') === 'attention';
  const initialFocus = initialAttention ? 'none' : (searchParams.get('filter') || 'none');
  // Window for the "new" (recently-created) filter, matching the dashboard tile's period.
  const windowDays = Number(searchParams.get('days')) || 7;
  const [assigneeFilter, setAssigneeFilter] = useState(searchParams.get('consultant') || 'All');
  const [attentionOnly, setAttentionOnly] = useState(initialAttention);
  const [settingNextFor, setSettingNextFor] = useState(null); // lead whose next step is being set from its card
  const [focus, setFocus] = useState(initialFocus); // none | unactioned | unassigned | no-next-step | exceptions | hot | new | booked

  // Next event per visible lead — one batch call, keyed by lead id. Falls
  // back to "unknown" (no chip) if the endpoint is not deployed yet.
  const [nextEvents, setNextEvents] = useState({});
  const [nextEventsLoaded, setNextEventsLoaded] = useState(false);

  // First-response threshold, from the dealership's own settings. 30 is the
  // seeded default and the fallback if the endpoint is not deployed — the
  // board must still render a sensible timer, just not a configured one.
  const [firstResponseMins, setFirstResponseMins] = useState(30);
  useEffect(() => {
    let cancelled = false;
    leadFollowUpService.getSla()
      .then((cfg) => {
        const v = Number(cfg?.firstResponseMinutes ?? cfg?.data?.firstResponseMinutes);
        if (!cancelled && Number.isFinite(v) && v > 0) setFirstResponseMins(v);
      })
      .catch(() => { /* keep the default; the board is not the place to surface this */ });
    return () => { cancelled = true; };
  }, []);

  // A minutes-scale counter has to actually move. Re-render every 30s rather
  // than every second: the chip shows whole minutes, so a faster tick would
  // cost renders and change nothing on screen.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const ids = leads.filter((l) => !isClosedStage(l.stage)).map((l) => l.id).filter(Boolean);
    if (!ids.length) { setNextEvents({}); setNextEventsLoaded(true); return undefined; }
    leadsService.nextEvents(ids)
      .then((map) => { if (!cancelled) { setNextEvents(map || {}); setNextEventsLoaded(true); } })
      .catch(() => { if (!cancelled) setNextEventsLoaded(false); });
    return () => { cancelled = true; };
  }, [leads]);

  const isUnactioned = (l) => {
    if (isClosedStage(l.stage)) return false;
    const last = l.lastActivityAt || l.createdAt;
    return !!last && new Date(last).getTime() < Date.now() - 24 * 60 * 60 * 1000;
  };
  const hasNoNextStep = (l) => nextEventsLoaded && !isClosedStage(l.stage) && !nextEvents[l.id];
  const matchesFocus = (l) => {
    switch (focus) {
      case 'unactioned': return isUnactioned(l);
      // Header counters made clickable: the filters return exactly what they count.
      case 'first-response': return !!firstResponse(l, firstResponseMins, now)?.breached;
      case 'sla': return isBreaching(l);
      // The four Dashboard-tile links below leave out Agent Test Console (simulated)
      // leads, exactly as the Dashboard's server-side counts do, so the list always
      // matches the number on the tile.
      case 'stalled': return !l.simulated && isStalledLead(l);
      case 'unassigned': return !isClosedStage(l.stage) && !l.assignedConsultantId;
      case 'no-next-step': return hasNoNextStep(l);
      case 'exceptions': return isUnactioned(l) || (!isClosedStage(l.stage) && !l.assignedConsultantId) || hasNoNextStep(l);
      // Dashboard-tile deep links:
      case 'hot': return !l.simulated && (l.score ?? 0) >= 80 && !isClosedStage(l.stage);
      case 'new': {        // created within the tile's window (default 7 days)
        if (l.simulated) return false;
        const created = l.createdAt ? new Date(l.createdAt).getTime() : 0;
        return !!created && created >= now - windowDays * 24 * 60 * 60 * 1000;
      }
      case 'booked': {
        if (l.simulated || l.stage !== 'DELIVERY_BOOKED') return false;
        // With a days value (the dashboard's Bookings tile) only the leads that
        // moved into Booked inside that window; without one, every Booked lead.
        if (!searchParams.get('days')) return true;
        const entered = l.stageEnteredAt ? new Date(l.stageEnteredAt).getTime() : 0;
        return !!entered && entered >= now - windowDays * 24 * 60 * 60 * 1000;
      }
      default: return true;
    }
  };
  // Labels for the dashboard-tile filters, shown as a dismissible chip so it's
  // obvious why the board is filtered and how to clear it.
  const DASH_FOCUS_LABELS = {
    hot: 'Hot leads (score ≥ 80)',
    new: `New leads · last ${windowDays}d`,
    booked: searchParams.get('days') ? `Booked · last ${windowDays}d` : 'Booked',
    stalled: `Stalled leads · no activity for ${STALLED_DAYS}+ days`,
    'first-response': `Unactioned · inbound past the ${firstResponseMins} min first-response target`,
    sla: 'SLA breaches · early-stage, no activity for 24h+',
  };
  const setFocusAndUrl = (f) => {
    setFocus(f);
    const next = new URLSearchParams(searchParams);
    if (f === 'none') next.delete('filter'); else next.set('filter', f);
    setSearchParams(next, { replace: true });
  };
  const [refreshing, setRefreshing] = useState(false);

  const consultantInitials = useMemo(() => {
    const m = new Map(consultantOptions.map((o) => [o.id, (o.primary || '?')
      .split(' ').map((w) => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()]));
    return (id) => (id ? (m.get(id) || '··') : null);
  }, [consultantOptions]);

  const onManualRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await refreshLeads?.();
    } finally {
      // Tiny delay so the spinner is visible even on lightning-fast networks.
      setTimeout(() => setRefreshing(false), 300);
    }
  };
  const [activeSource, setActiveSource] = useState('All');
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [snap, setSnap] = useState(null);
  // Set when a drag lands on Lost — we must collect a lost-reason code before
  // the backend will accept the transition (LOST_REASON_REQUIRED).
  const [pendingLost, setPendingLost] = useState(null);

  const [view, setView] = useState('board');     // board | table

  // DEFERRED (client request pending): "Show the latest N per column" — a
  // display limit (counts and filters still cover every lead). Commented out;
  // the full version is saved in _parked/lead-accelerator-load-all/.
  // const COLUMN_LIMIT_KEY = 'spectraiq.leads.columnLimit';
  // const [columnLimit, setColumnLimit] = useState(() => {
  //   try {
  //     const v = Number(localStorage.getItem(COLUMN_LIMIT_KEY));
  //     return [0, 20, 50, 100].includes(v) ? v : 0;
  //   } catch (_) { return 0; }
  // });
  // const [expandedCols, setExpandedCols] = useState({});   // stage -> user chose "Show all"
  // const pickColumnLimit = (n) => {
  //   setColumnLimit(n);
  //   setExpandedCols({});
  //   try { localStorage.setItem(COLUMN_LIMIT_KEY, String(n)); } catch (_) { /* private window */ }
  // };
  // const recency = (l) => new Date(l.lastActivityAt || l.createdAt || 0).getTime();
  // const shownItems = (col) => {
  //   if (!columnLimit || expandedCols[col.stage] || col.items.length <= columnLimit) return col.items;
  //   return [...col.items].sort((a, b) => recency(b) - recency(a)).slice(0, columnLimit);
  // };
  // const hiddenIn = (col) => col.items.length - shownItems(col).length;
  const [query, setQuery] = useState('');
  // Deep link from the dashboard CRM funnel: /leads?stage=<STAGE> opens the board
  // filtered to that one stage. Ignored unless it's a real stage.
  const [stageFilter, setStageFilter] = useState(() => {
    const s = searchParams.get('stage');
    return s && LEAD_STAGES.includes(s) ? s : 'All';
  });
  // E4 (Ajay #4 / T035): New / Used sits ON the board, not buried in a panel —
  // it is the first cut a dealer principal makes, not the last.
  const [categoryFilter, setCategoryFilter] = useState('All');
  // E5 / T040: "what is the AI still holding" is the question a manager asks
  // out loud, so it belongs on the board next to the other first cuts.
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [sortKey, setSortKey] = useState('lastActivityAt');
  const [sortDir, setSortDir] = useState('desc');

  // Deep link from the dashboard's "New lead" quick action: /leads?new=1
  // auto-opens the create dialog, then clears the param so it doesn't reopen.
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setNewLeadOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    dashboardService.snapshot({ days: 7 })
      .then((s) => { if (!cancelled) setSnap(s); })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  // SLA breach count: leads still in an early stage whose last activity is
  // older than 24h (Sprint-3 SLA target: 1h first reply, 24h next step).
  // Measured on lastActivityAt — createdAt never moves, so the old version
  // counted every aged lead forever even if it was being worked today.
  // Inbound leads past the first-response target, right now.
  const unactionedNow = useMemo(
    () => leads.filter((l) => firstResponse(l, firstResponseMins, now)?.breached).length,
    [leads, firstResponseMins, now],
  );

  // Same predicate as the card's breach chip (isBreaching), so the header count,
  // the "SLA breach" filter and the chips on the cards can never disagree.
  const slaBreaches = useMemo(() => leads.filter(isBreaching).length, [leads, now]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * One filtering pipeline feeding BOTH views, so the board and the table can
   * never disagree about what's in scope.
   */
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads
      .map(l => ({ ...l, customer: customers.find(c => c.id === l.customerId) }))
      .filter((l) => {
        if (activeSource !== 'All' && l.source !== activeSource) return false;
        if (stageFilter !== 'All' && l.stage !== stageFilter) return false;
        if (assigneeFilter === 'Unassigned' && l.assignedConsultantId) return false;
        if (assigneeFilter !== 'All' && assigneeFilter !== 'Unassigned'
          && l.assignedConsultantId !== assigneeFilter) return false;
        if (attentionOnly && !unreadLeadIds.has(String(l.id))) return false;
        if (ownerFilter !== 'All' && ownershipOf(l).key !== ownerFilter) return false;
        if (categoryFilter !== 'All') {
          // 'UNCLASSIFIED' is a real choice, not a fallback: it is how you find
          // the leads whose capture needs fixing.
          const cat = l.vehicleCategory || '';
          if (categoryFilter === 'UNCLASSIFIED' ? !!cat : cat !== categoryFilter) return false;
        }
        if (!matchesFocus(l)) return false;
        if (!q) return true;
        const hay = [
          l.customer?.name, l.customer?.phone, l.customer?.email,
          l.name, l.vehicle, l.vehicleMake, l.vehicleModel,
          l.source, l.vehicleCategory, STAGE_META[l.stage]?.label, l.notes, l.sourceReference, l.id,
        ].filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      });
  }, [leads, customers, activeSource, stageFilter, categoryFilter, ownerFilter, assigneeFilter, attentionOnly, unreadLeadIds, query, focus, nextEvents, nextEventsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leads the AI has flagged for human attention (unread handoff/escalation alerts).
  // Total flagged leads from the alerts endpoint — independent of how many lead
  // pages are loaded, so this matches the nav badge (which reads the same set).
  const attentionCount = useMemo(() => unreadLeadIds.size, [unreadLeadIds]);

  const cols = useMemo(() => LEAD_STAGES.map(stage => {
    const items = visible.filter(l => l.stage === stage);
    let value = 0;
    let estimated = false;
    for (const l of items) {
      const dv = dealValue(l, l.vehicleOfInterestId ? vehiclesById.get(l.vehicleOfInterestId) : null);
      value += dv.value;
      if (dv.estimated) estimated = true;
    }
    return { stage, items, value, estimated };
  }), [visible, vehiclesById]);

  const total = visible.length;

  /* ─── table sorting ─── */
  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (l) => {
      switch (sortKey) {
        case 'name': return (l.customer?.name || l.name || '').toLowerCase();
        case 'score': return l.score ?? 0;
        case 'stage': return LEAD_STAGES.indexOf(l.stage);
        case 'source': return l.source || '';
        case 'vehicle': return (l.vehicle || '').toLowerCase();
        case 'createdAt': return new Date(l.createdAt || 0).getTime();
        default: return new Date(l.lastActivityAt || l.createdAt || 0).getTime();
      }
    };
    return [...visible].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [visible, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' || key === 'source' || key === 'vehicle' ? 'asc' : 'desc'); }
  };
  const sortArrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '');

  /* ─── HTML5 drag-and-drop handlers ─── */
  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData(DND_MIME, leadId);
    e.dataTransfer.setData('text/plain', leadId); // fallback
    e.dataTransfer.effectAllowed = 'move';
    setDraggingId(leadId);
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };
  const onColumnDragOver = (e, stage) => {
    if (!e.dataTransfer.types.includes(DND_MIME) && !e.dataTransfer.types.includes('text/plain')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const onColumnDragLeave = (e, stage) => {
    // Only clear if we're truly leaving the column (not entering a child).
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (dragOverStage === stage) setDragOverStage(null);
  };
  const onColumnDrop = async (e, stage) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData('text/plain');
    setDragOverStage(null);
    setDraggingId(null);
    if (!leadId) return;

    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.stage === stage) return;

    const cust = customers.find(c => c.id === lead.customerId);
    const who = lead.customerName || cust?.name || lead.name || lead.id;

    // The backend refuses to move a closed lead (LEAD_CLOSED) — say so up
    // front instead of firing a request we know will fail.
    if (isClosedStage(lead.stage)) {
      toast(
        `${who} is ${STAGE_META[lead.stage]?.label} — reopen the lead before moving it.`,
        { variant: 'warning' },
      );
      return;
    }

    // LOST needs a reason code; hand off to the modal instead of guessing one.
    if (stage === 'LOST') {
      setPendingLost(lead);
      return;
    }

    try {
      await moveLead(leadId, stage, {});
      toast(`${who} → ${STAGE_META[stage]?.label || stage}`, { variant: 'success' });
    } catch (err) {
      toast(apiErrorMessage(err, `Couldn’t move ${who}`), { variant: 'danger' });
    }
  };

  return (
    <>
      <TopBar
        crumbs={[['Lead Accelerator', '/leads'], view === 'board' ? 'Board' : 'Table']}
        right={
          <>
            {/* View switch */}
            <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--bg-glass)', borderRadius: 999, border: '1px solid var(--border-color)' }}>
              {[['board', 'Board'], ['table', 'Table']].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className="btn small"
                  style={{
                    border: 'none',
                    background: view === v ? 'var(--accent-primary)' : 'transparent',
                    color: view === v ? '#fff' : 'var(--text-secondary)',
                    borderRadius: 999,
                    padding: '4px 12px',
                  }}
                >{label}</button>
              ))}
            </div>
            <button
              className="btn small"
              onClick={onManualRefresh}
              disabled={refreshing}
              title="Refresh leads (auto-refreshes every 15s)"
            >
              <span
                style={{
                  display: 'inline-block',
                  transition: 'transform 600ms ease',
                  transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                }}
                aria-hidden="true"
              >↻</span> Refresh
            </button>
            <Link to="/customers" className="btn small">All customers</Link>
            <button className="btn small" onClick={() => setImportOpen(true)}>⭱ Import</button>
            <button className="btn small primary" onClick={() => setNewLeadOpen(true)}>
              + New lead
            </button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Search + filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '0 1 320px', minWidth: 200 }}>
            <span
              aria-hidden="true"
              style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)', fontSize: 12, pointerEvents: 'none',
              }}
            >⌕</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, phone, vehicle, notes…"
              aria-label="Search leads"
              style={{
                width: '100%', padding: '7px 28px 7px 26px',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)', fontSize: 13, outline: 'none',
              }}
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Clear search"
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: 'var(--text-tertiary)', fontSize: 12, lineHeight: 1, padding: 4,
                }}
              >✕</button>
            )}
          </div>

          <select
            className="btn small"
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            aria-label="Filter by stage"
          >
            <option value="All">All stages</option>
            {LEAD_STAGES.map(s => (
              <option key={s} value={s}>{STAGE_META[s]?.label || s}</option>
            ))}
          </select>

          <select
            className="btn small"
            value={assigneeFilter}
            onChange={(e) => setAssigneeFilter(e.target.value)}
            aria-label="Filter by assigned consultant"
          >
            <option value="All">All consultants</option>
            <option value="Unassigned">Unassigned</option>
            {consultantOptions.map(o => (
              <option key={o.id} value={o.id}>{o.primary}</option>
            ))}
          </select>

          <button
            type="button"
            className={`btn small ${attentionOnly ? 'primary' : ''}`}
            onClick={() => setAttentionOnly(v => !v)}
            aria-pressed={attentionOnly}
            title={"A lead needs attention when it has an unread alert. Alerts are raised for: "
              + "first-response SLA breach, no activity for too long (unactioned), unassigned, "
              + "no next step set, AI nurture expiring, a human-handoff request, a reply on a paused "
              + "lead, or a due task reminder. The flag clears when you open the lead (marks it read)."}
            style={!attentionOnly && attentionCount > 0
              ? { borderColor: 'var(--danger)', color: 'var(--danger)' } : undefined}
          >
            🔔 Needs attention{attentionCount > 0 ? ` · ${attentionCount}` : ''}
          </button>

          {/* Exception focus — what the dashboards link to. */}
          <div className="flex-row" style={{ gap: 4 }} role="group" aria-label="Exception focus">
            {[
              ['exceptions', 'Exceptions'],
              ['unactioned', 'Idle 24h+'],
              // Stalled has no pill here on purpose: the extra pill pushed the whole
              // group onto a second row. It is reached from the dashboard link
              // (/leads?filter=stalled) and shows as a dismissible chip, like Hot / New / Booked.
              ['unassigned', 'Unassigned'],
              ['no-next-step', 'No next step'],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`pill${focus === k ? ' active' : ''}`}
                aria-pressed={focus === k}
                onClick={() => setFocusAndUrl(focus === k ? 'none' : k)}
                title={k === 'no-next-step' && !nextEventsLoaded
                  ? 'Next-event data not available yet'
                  : k === 'unactioned' ? 'Any open lead with no activity for 24h+ (all stages). The header "SLA breaches" counts early-stage leads only.'
                    : undefined}
                disabled={k === 'no-next-step' && !nextEventsLoaded}
              >{label}</button>
            ))}
          </div>

          {/* Active dashboard-tile filter (hot / new / booked) — a clearly
              labelled, dismissible chip so the drill-in from a dashboard tile is
              obvious and easy to clear. */}
          {DASH_FOCUS_LABELS[focus] && (
            <button
              type="button"
              className="pill active"
              onClick={() => setFocusAndUrl('none')}
              title="Clear this filter"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {DASH_FOCUS_LABELS[focus]} <span aria-hidden="true">✕</span>
            </button>
          )}

          <div className="flex-row" style={{ gap: 4 }} role="group" aria-label="Filter by vehicle category">
            {CATEGORY_FILTERS.map(([v, label]) => (
              <button
                key={v}
                type="button"
                className={`pill${categoryFilter === v ? ' active' : ''}`}
                aria-pressed={categoryFilter === v}
                onClick={() => setCategoryFilter(v)}
                style={categoryFilter !== v && CATEGORY_META[v]
                  ? { borderColor: CATEGORY_META[v].color, color: CATEGORY_META[v].color }
                  : undefined}
              >
                {label}
              </button>
            ))}
          </div>

          <select
            className="btn small"
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            aria-label="Filter by who owns the conversation"
          >
            <option value="All">All owners</option>
            <option value="AI">AI only</option>
            <option value="HUMAN">Human only</option>
          </select>

          <select
            className="btn small"
            value={activeSource}
            onChange={(e) => setActiveSource(e.target.value)}
            aria-label="Filter by source"
          >
            {SOURCES.map(([v, label]) => (
              <option key={v} value={v}>{v === 'All' ? 'All sources' : label}</option>
            ))}
          </select>

          {(query || stageFilter !== 'All' || categoryFilter !== 'All' || ownerFilter !== 'All' || activeSource !== 'All' || assigneeFilter !== 'All' || attentionOnly || focus !== 'none') && (
            <button
              className="btn small"
              onClick={() => { setQuery(''); setStageFilter('All'); setActiveSource('All'); setAssigneeFilter('All'); setAttentionOnly(false); setFocusAndUrl('none'); }}
            >
              Clear filters
            </button>
          )}

          {/* DEFERRED (client request pending): "Show latest N per column" selector.
          {view === 'board' && (
            <label className="mono text-xs text-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Show
              <select
                className="input input-compact"
                value={columnLimit}
                onChange={(e) => pickColumnLimit(Number(e.target.value))}
                aria-label="Cards shown per column"
                title="How many cards to draw in each column (newest activity first). Totals are not affected."
              >
                <option value={0}>All</option>
                <option value={20}>Latest 20</option>
                <option value={50}>Latest 50</option>
                <option value={100}>Latest 100</option>
              </select>
              per column
            </label>
          )}
          */}

          <span style={{ marginLeft: 'auto' }} className="mono text-xs">
            {total} lead{total === 1 ? '' : 's'}
            {total !== leads.length && ` of ${leads.length}`}
            {(() => {
              // Say how many filters are narrowing the board, so a short list is never a mystery.
              const active = [query, stageFilter !== 'All', categoryFilter !== 'All', ownerFilter !== 'All',
                activeSource !== 'All', assigneeFilter !== 'All', attentionOnly, focus !== 'none'].filter(Boolean).length;
              return active > 0 ? ` · ${active} filter${active === 1 ? '' : 's'} on` : '';
            })()}
            {snap?.hotLeads != null && ` · ${snap.hotLeads} hot`}
            {view === 'board' && ' · drag to move stage'}
            {' · '}
            <button
              type="button"
              className={unactionedNow > 0 ? 'text-danger' : 'text-success'}
              style={{ all: 'unset', cursor: 'pointer', textDecoration: focus === 'first-response' ? 'underline' : 'none' }}
              title={`Inbound leads past the ${firstResponseMins} minute first-response target — click to list them`}
              aria-pressed={focus === 'first-response'}
              onClick={() => setFocusAndUrl(focus === 'first-response' ? 'none' : 'first-response')}
            >
              {unactionedNow} unactioned
            </button>
            {' · SLA · '}
            <button
              type="button"
              className={slaBreaches > 0 ? 'text-danger' : 'text-success'}
              style={{ all: 'unset', cursor: 'pointer', textDecoration: focus === 'sla' ? 'underline' : 'none' }}
              title="Early-stage leads with no activity for 24h+ — click to list them"
              aria-pressed={focus === 'sla'}
              onClick={() => setFocusAndUrl(focus === 'sla' ? 'none' : 'sla')}
            >
              {slaBreaches} breach{slaBreaches === 1 ? '' : 'es'}
            </button>
          </span>
        </div>

        {/* Key — what the colours and chips on a card mean, in one place. */}
        <details className="mono text-xs text-muted" style={{ margin: '0 0 6px' }}>
          <summary style={{ cursor: 'pointer' }}>ⓘ Key to scores, colours and chips</summary>
          <div style={{ display: 'grid', gap: 3, padding: '6px 0 2px', lineHeight: 1.5 }}>
            <div>
              <span style={{ color: 'var(--success)' }}>●</span> Hot — score 80+ &nbsp;
              <span style={{ color: 'var(--warning)' }}>●</span> Warm — 41–79 &nbsp;
              <span style={{ color: 'var(--danger)' }}>●</span> Cold — 40 or below (card edge and score badge)
            </div>
            <div>Unactioned — an inbound lead past the {firstResponseMins} minute first-response target</div>
            <div>⏱ SLA — an early-stage lead with no activity for 24h or more</div>
            <div>No next step — the lead has no open task scheduled (use + Set)</div>
            <div>AI / Human — who is replying to the customer right now (separate from the assigned consultant)</div>
            <div>Consultant name — the salesperson responsible for the lead</div>
          </div>
        </details>

        {view === 'board' && (
          <div
            className="kanban-row"
            style={{
              display: 'flex', gap: 12, flex: 1, minHeight: 0,
              alignItems: 'stretch',
              // Ten stages don't fit on a laptop — scroll horizontally rather
              // than crushing every column to an unreadable width.
              overflowX: 'auto', paddingBottom: 4,
            }}
          >
            {cols.map((c, ci) => (
              <div
                key={c.stage}
                className={`kanban-col animate-in ${dragOverStage === c.stage ? 'drop-target' : ''}`}
                style={{
                  animationDelay: `${ci * 0.05}s`,
                  flex: '0 0 224px',
                  minWidth: 224,
                  // Terminal columns read as "out of play".
                  opacity: isClosedStage(c.stage) ? 0.9 : 1,
                }}
                onDragOver={(e) => onColumnDragOver(e, c.stage)}
                onDragLeave={(e) => onColumnDragLeave(e, c.stage)}
                onDrop={(e) => onColumnDrop(e, c.stage)}
              >
                <div className="kanban-col-header">
                  <span
                    className="mono text-xs"
                    style={{ letterSpacing: 1, color: STAGE_META[c.stage]?.color || 'var(--text-tertiary)' }}
                    title={c.stage}
                  >
                    {(STAGE_META[c.stage]?.label || c.stage).toUpperCase()}
                  </span>
                  <span
                    className="mono text-xs text-muted"
                    title={c.estimated ? 'Estimate: some leads in this stage have no driveaway price and use the default transaction value' : undefined}
                  >
                    {c.items.length}{c.items.length > 0 && ` · ${fmtMoneyEst(c.value, c.estimated)}`}
                  </span>
                </div>
                {/* DEFERRED: with the column limit on this reads shownItems(c).map(...) */}
                {c.items.map(it => {
                  const cust = it.customer;
                  const name = cust?.displayName || it.name || 'Unknown';
                  const tier = scoreTier(it.score);
                  const unread = unreadLeadIds.has(String(it.id));
                  const channel = cust?.preferences?.channel;
                  const isVip = cust?.tags?.includes('VIP');
                  return (
                    <Link
                      key={it.id}
                      to={`/leads/${it.id}`}
                      draggable
                      onDragStart={(e) => onDragStart(e, it.id)}
                      onDragEnd={onDragEnd}
                      className={`kanban-card lead-card ${tier} ${draggingId === it.id ? 'dragging' : ''}`}
                    >
                      <div className="lc-top">
                        <div className="lc-avatar" aria-hidden="true">{cust?.initials || initialsOf(name)}</div>
                        <div className="lc-id">
                          <div className="lc-name">
                            {unread && (
                              <span className="lc-unread-dot animate-pulse" aria-hidden="true" title="Unread alert on this lead" />
                            )}
                            <span className="lc-name-text" title={name}>{name}</span>
                            {isVip && <span className="lc-vip">VIP</span>}
                          </div>
                          <div className="lc-vehicle" title={it.vehicle}>
                            {/* E4 / T033: category readable without opening the
                              record. A 3px stripe rather than another chip —
                              the card already carries score, SLA, channel and
                              next step, and a fifth badge would cost more
                              attention than the fact is worth. */}
                            {/* T034: the same 3px stripe, now the reclassify
                              control. Rendered for unclassified leads too —
                              those are the ones that most need fixing, and a
                              missing stripe left them with no affordance. */}
                            <CategoryStripe lead={it} onChanged={refreshLeads} />
                            {it.vehicle || '—'}
                          </div>
                        </div>
                        <div className={`lc-score ${tier}`} title={`Lead score ${it.score}/100 — ${TIER_MEANING[tier]}`}>
                          <span className="lc-score-n">{it.score}</span>
                          <span className="lc-score-l">score</span>
                        </div>
                      </div>

                      {it.notes && !isSystemNote(it.notes) && <div className="lc-note" title={it.notes}>{it.notes}</div>}

                      {/* What happens next — or the warning that nothing does.
                          "+ Set" opens the next-step dialog right here (the card
                          itself is a link, so the click must not navigate). */}
                      {nextEventsLoaded && !isClosedStage(it.stage) && (
                        <div style={{ margin: '4px 0 2px', fontSize: 11 }}>
                          <NextEventChip
                            nextEvent={nextEvents[it.id] || null}
                            compact
                            onSet={(e) => { e.preventDefault(); e.stopPropagation(); setSettingNextFor(it); }}
                          />
                        </div>
                      )}

                      <div className="lc-meta">
                        {/* Owner — a proper avatar badge reads faster than a text chip. */}
                        {it.assignedConsultantId ? (
                          <span
                            title={`Assigned to ${consultantOptions.find(o => o.id === it.assignedConsultantId)?.primary || 'a consultant'}`}
                            style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              background: 'var(--accent-primary)', color: '#fff', fontSize: 9, fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >{consultantInitials(it.assignedConsultantId)}</span>
                        ) : (
                          <span
                            title="Unassigned — open the lead to assign a consultant"
                            style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              border: '1px dashed var(--warning)', color: 'var(--warning)', fontSize: 13, lineHeight: 1,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            }}
                          >+</span>
                        )}

                        {/* The consultant's name in words, not just initials — the
                          avatar alone made two people with the same initials
                          indistinguishable. */}
                        {it.assignedConsultantId ? (
                          <span className="lc-chip" title="Assigned sales consultant">
                            {consultantOptions.find(o => o.id === it.assignedConsultantId)?.primary?.split(' ')[0] || '…'}
                          </span>
                        ) : (
                          <span className="lc-chip" title="No consultant assigned yet" style={{ color: 'var(--warning)' }}>Unassigned</span>
                        )}

                        {/* E5 / T040: who is talking to this customer. First chip
                          in the row because it is the question Ajay could not
                          answer from the board at all. */}
                        <OwnershipChip lead={it} compact />

                        {channel && (
                          <span className="lc-chip" title={`Prefers ${channel}`}>{channel}</span>
                        )}

                        {/* First response — Inbound only, minutes-scale (T019). */}
                        {(() => {
                          const fr = firstResponse(it, firstResponseMins, now);
                          if (!fr) return null;
                          const tone = fr.breached
                            ? { bg: 'rgba(239,68,68,0.14)', fg: 'var(--danger)' }
                            : fr.warning
                              ? { bg: 'rgba(245,158,11,0.14)', fg: 'var(--warning)' }
                              : { bg: 'rgba(148,163,184,0.14)', fg: 'var(--text-secondary)' };
                          return (
                            <span
                              className="lc-chip"
                              title={fr.breached
                                ? `Unactioned for ${shortMins(fr.mins)} — past the ${firstResponseMins} minute first-response target`
                                : `Waiting ${shortMins(fr.mins)} of ${firstResponseMins} minutes for a first response`}
                              style={{ background: tone.bg, color: tone.fg, borderColor: 'transparent', fontWeight: fr.breached ? 600 : 400 }}
                            >
                              {fr.breached ? '⏱ ' : ''}Unactioned {shortMins(fr.mins)}
                            </span>
                          );
                        })()}

                        {isBreaching(it) && (
                          <span className="lc-chip" title="No activity in over 24h — SLA breach"
                            style={{ background: 'rgba(239,68,68,0.12)', color: 'var(--danger)', borderColor: 'transparent' }}>⏱ SLA</span>
                        )}

                        <span className="lc-time" title={`In ${STAGE_META[it.stage]?.label || it.stage} for ${daysSince(it.stageEnteredAt) ?? '—'}d`}>
                          {relTime(it.createdAt)}
                        </span>
                      </div>
                    </Link>
                  );
                })}
                {/* DEFERRED (client request pending): "Showing N of M · Show all" button.
                {hiddenIn(c) > 0 && (
                  <button
                    type="button"
                    className="btn small"
                    style={{ margin: '6px auto 2px', display: 'block' }}
                    title="Every lead in this column is still counted above — this only limits how many cards are drawn"
                    onClick={() => setExpandedCols(prev => ({ ...prev, [c.stage]: true }))}
                  >
                    Showing {shownItems(c).length} of {c.items.length} · Show all
                  </button>
                )}
                */}
                {c.items.length === 0 && (
                  <div className={`kanban-empty mono text-xs text-muted ${dragOverStage === c.stage ? 'drop-target' : ''}`}>
                    {dragOverStage === c.stage
                      ? (c.stage === 'LOST' ? 'Drop to mark lost' : 'Drop to move here')
                      : 'No leads currently in this stage'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── Table view ── */}
        {view === 'table' && (
          <div className="card" style={{ padding: 0, overflow: 'auto', flex: 1, minHeight: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  {[
                    ['name', 'Customer'],
                    ['vehicle', 'Vehicle'],
                    ['stage', 'Stage'],
                    ['score', 'Score'],
                    ['source', 'Source'],
                    ['lastActivityAt', 'Last activity'],
                    ['createdAt', 'Created'],
                  ].map(([key, label]) => (
                    <th
                      key={key}
                      onClick={() => toggleSort(key)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title={`Sort by ${label.toLowerCase()}`}
                    >
                      {label}{sortArrow(key)}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="empty-state" style={{ padding: 32 }}>
                        <div className="title">
                          {leads.length === 0 ? 'No leads yet' : 'Nothing matches those filters'}
                        </div>
                        <div className="desc">
                          {leads.length === 0
                            ? 'Create one manually, or wait for the next inbound enquiry.'
                            : 'Try a different search, or clear the filters.'}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : sorted.map((l) => {
                  const cust = l.customer;
                  const name = cust?.displayName || l.name || 'Unknown';
                  const unread = unreadLeadIds.has(String(l.id));
                  return (
                    <tr
                      key={l.id}
                      onClick={() => navigate(`/leads/${l.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {unread && (
                            <span className="lc-unread-dot animate-pulse" aria-hidden="true" title="Unread alert" />
                          )}
                          <div>
                            <div style={{ fontWeight: 600 }}>{name}</div>
                            <div className="mono text-xs text-muted">{cust?.phone || cust?.email || '—'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="mono text-xs">{l.vehicle || '—'}</td>
                      <td>
                        <span
                          className="pill filled"
                          style={{ background: 'var(--bg-glass)', color: STAGE_META[l.stage]?.color }}
                        >
                          {STAGE_META[l.stage]?.label || l.stage}
                        </span>
                      </td>
                      <td
                        className="mono text-xs"
                        // Same tiers and colours as the board card (hot green, warm amber,
                        // cold red) — the table used to show warm as grey.
                        title={`Lead score ${l.score}/100 — ${TIER_MEANING[scoreTier(l.score)]}`}
                        style={{
                          fontWeight: 700,
                          color: l.score >= 80 ? 'var(--success)' : l.score <= 40 ? 'var(--danger)' : 'var(--warning)',
                        }}
                      >
                        {l.score}
                      </td>
                      <td className="mono text-xs text-muted" title={l.source}>{sourceLabel(l.source)}</td>
                      <td className="mono text-xs text-muted">{relTime(l.lastActivityAt || l.createdAt)}</td>
                      <td className="mono text-xs text-muted">{relTime(l.createdAt)}</td>
                      <td>
                        <Link
                          to={`/leads/${l.id}`}
                          className="mono text-xs"
                          style={{ color: 'var(--accent-primary-hover)' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dropping onto Lost opens the reason picker — the backend rejects a
          LOST transition that arrives without an active lostReasonCode. */}
      <NextEventModal
        open={!!settingNextFor}
        leadId={settingNextFor?.id || null}
        defaultAssigneeId={settingNextFor?.assignedConsultantId || null}
        onClose={() => setSettingNextFor(null)}
        // Re-pull the leads: the next-event map is keyed off the lead list, so a
        // fresh list refreshes the card's chip.
        onSaved={() => refreshLeads?.()}
      />

      <StageChangeModal
        open={!!pendingLost}
        lead={pendingLost}
        initialTarget="LOST"
        onClose={() => setPendingLost(null)}
        onChange={async (to, opts) => {
          const lead = pendingLost;
          await moveLead(lead.id, to, opts);
          const cust = customers.find(c => c.id === lead.customerId);
          toast(`${lead.customerName || cust?.name || lead.id} → ${STAGE_META[to]?.label || to}`, {
            variant: to === 'LOST' ? 'warning' : 'success',
          });
        }}
      />

      {/* Mounted only while open — its lookups fetch on mount. */}
      {newLeadOpen && (
        <NewLeadModal
          onClose={() => setNewLeadOpen(false)}
          onCreated={async (lead) => {
            toast('Lead created', { variant: 'success' });
            await refreshLeads?.();
            if (lead?.id) navigate(`/leads/${lead.id}`);
          }}
        />
      )}

      <BulkImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import leads"
        columns={LEAD_IMPORT_COLUMNS}
        sampleRow={LEAD_SAMPLE_ROW}
        templateName="leads-template.csv"
        onImport={(file) => leadsService.importFile(file)}
        onDone={() => refreshLeads?.()}
      />
    </>
  );
}
